import type { TFunction } from 'i18next';

import {
  calculateAge,
  countryDisplayName,
  getDanceStyleLabels,
  isFieldVisible,
  languageDisplayName,
} from '@kit/artists';

import type {
  DancerExperience,
  PublicDancerProfile,
  ViewContext,
} from './types';

/**
 * One "playbill" identity cell — a mono uppercase label + its value.
 *
 * Lives in this framework-agnostic module (no `'use client'`, no
 * `react-i18next`) so BOTH the client `DancerIdentityStrip` and the SERVER
 * `DancerHero` can compose cells from a single source of truth. The `t`
 * function is passed in, so a server component supplies its server i18n
 * instance and a client component supplies the hook's — neither drags a
 * client-only import into the other's module graph.
 */
export interface Cell {
  label: string;
  value: string;
}

/** Number of style labels shown in a cell before truncating to "+N more". */
export const STYLES_CELL_MAX = 3;

/**
 * Compose the identity cells for a dancer, in casting-scan order:
 *   Age → Height → Level → Affiliation → Styles → Based in → Nationality →
 *   Languages, then registry-gated appearance/measurement cells.
 *
 * Registry-gated cells (Eyes, Hair, Bust, Waist, Hips) only append when a
 * `viewContext` is supplied AND the field passes its per-surface visibility
 * gate; they always come after the always-on essentials, so callers taking a
 * leading slice (e.g. the hero's first four) never surface gated data.
 */
export function composeCells(
  d: PublicDancerProfile,
  locale: string,
  viewContext: ViewContext | undefined,
  experiences: DancerExperience[] | undefined,
  t: TFunction,
): Cell[] {
  const cells: Cell[] = [];

  // 1. Age — first casting filter in every dance industry pipeline.
  // BAL-248 §4b: show age only up to 45; for older dancers hide it
  // entirely (the exact DOB is never rendered — privacy).
  // Server-computed age wins; the raw DOB is withheld on the public surface.
  const age = d.age ?? calculateAge(d.date_of_birth);
  if (age !== null && age <= 45) {
    cells.push({
      label: t('dancer:publicView.identityStrip.age'),
      value: `${age}`,
    });
  }

  // 2. Height — second filter (partnering, costume, ensemble cohesion).
  // Surfaces both metric + imperial so US-market casting (still ft/in
  // by convention) reads at a glance without a unit toggle.
  //
  // AND-gated on `measurements_public`, exactly like bust/waist/hips below.
  // It was NOT, until 2026-07-29 — height rendered whenever a value existed.
  // The migration introducing the toggle defines it as covering "body
  // measurements (height/weight/bust/waist/hips)" with DEFAULT FALSE
  // (20260513120000_unify_dancer_visibility.sql:43-46), so every profile with
  // a height and no explicit opt-in was displaying it: ~4,800 public profiles.
  //
  // The registry ceiling for `heightCm` is ALWAYS, which permits the field on
  // this surface. The per-dancer toggle is the other half of the documented
  // AND-gate, and was simply never applied at this call site.
  const measurementsVisible =
    viewContext?.viewer === 'admin' ? true : (d.measurements_public ?? false);
  // Height has a WIDER audience than the other measurements, and only height.
  // `competition_snapshot` records height YES (2026-05-02, "industry standard for
  // dance") and omits weight/bust/waist/hips entirely, so the hiring-side tier —
  // admin, or staff of an organization the dancer applied to — sees height while
  // the measurement sheet stays behind `measurements_public`. Product decision
  // 2026-07-30. The tier is named `recruiter`: any client casting-team member or
  // organization member, renamed from `director` in Phase 2 of
  // WIP_retiring_director_vocabulary_2026_07_30.
  const heightVisible =
    measurementsVisible || viewContext?.viewer === 'recruiter';
  if (d.height_cm && heightVisible) {
    cells.push({
      label: t('dancer:publicView.identityStrip.height'),
      value: formatHeight(d.height_cm),
    });
  }

  // 3. Level — slot fit (Student / Apprentice / Pro / Principal).
  if (d.dance_level) {
    cells.push({
      label: t('dancer:publicView.identityStrip.level'),
      value: capitalise(d.dance_level),
    });
  }

  // 3b. Affiliation — per BAL-198, the company+rank (pro) or school
  //     (student) sub-line lives in the identity strip so casting
  //     recruiters see the current engagement at a glance.
  const affiliation = composeAffiliation(d, experiences, t);
  if (affiliation) {
    cells.push(affiliation);
  }

  // 4. Styles — actual labels, not a count. Truncated to 3 + "+N more"
  //    so the cell stays scannable on profiles with deep repertoire.
  if (d.dance_styles && d.dance_styles.length > 0) {
    const labels = getDanceStyleLabels(
      d.dance_styles as Parameters<typeof getDanceStyleLabels>[0],
    );
    const visible = labels.slice(0, STYLES_CELL_MAX);
    const remainder = labels.length - visible.length;
    cells.push({
      label: t('dancer:publicView.identityStrip.styles'),
      value:
        remainder > 0
          ? `${visible.join(' · ')} +${remainder}`
          : visible.join(' · '),
    });
  }

  // 5. Based in — combine city + country into one cell. If the dancer
  //    has geographic_availability set, append it parenthetically as
  //    the reach signal ("Paris, France · Worldwide").
  if (d.country_of_residence || d.city) {
    const base = [d.city, countryDisplayName(d.country_of_residence, locale)]
      .filter(Boolean)
      .join(', ');
    const reach =
      d.geographic_availability && d.geographic_availability.length > 0
        ? ` · ${d.geographic_availability.join(', ')}`
        : '';
    cells.push({
      label: t('dancer:publicView.identityStrip.basedIn'),
      value: `${base}${reach}`,
    });
  } else if (
    d.geographic_availability &&
    d.geographic_availability.length > 0
  ) {
    // Reach without a home base — still surface it (e.g. travelling
    // dancer with no fixed residence).
    cells.push({
      label: t('dancer:publicView.identityStrip.availableIn'),
      value: d.geographic_availability.join(', '),
    });
  }

  // 5b. Nationality / Nationalities — BAL-248 §4c: sits under "Based in"
  //     in the Main Information strip. Locale-resolved country names.
  if (d.nationalities && d.nationalities.length > 0) {
    const labels = d.nationalities.map((code) =>
      countryDisplayName(code, locale),
    );
    cells.push({
      label:
        d.nationalities.length > 1
          ? t('dancer:publicView.identityStrip.nationalities')
          : t('dancer:publicView.identityStrip.nationality'),
      value: labels.join(' · '),
    });
  }

  // 6. Languages — locale-resolved display names (BAL-199). Skip when empty.
  if (d.languages && d.languages.length > 0) {
    const labels = d.languages.map((code) => languageDisplayName(code, locale));
    cells.push({
      label: t('dancer:publicView.identityStrip.languages'),
      value: labels.join(' · '),
    });
  }

  // Registry-gated appearance + measurement cells. Eye / hair colour are
  // ALWAYS-visible per the registry; body measurements (bust / waist /
  // hips) gate to OWNER_OR_RECRUITER and additionally to the dancer's
  // `measurements_public` opt-in (admin bypasses). When `viewContext` is
  // omitted (e.g. the hero's leading-slice call), no registry cells render.
  if (viewContext) {
    if (d.eye_color && isFieldVisible('eyeColor', viewContext)) {
      cells.push({
        label: t('dancer:publicView.identityStrip.eyes'),
        value: capitalise(d.eye_color),
      });
    }
    if (d.hair_color && isFieldVisible('hairColor', viewContext)) {
      cells.push({
        label: t('dancer:publicView.identityStrip.hair'),
        value: capitalise(d.hair_color),
      });
    }
    // Identical to `measurementsVisible` hoisted at the height cell above.
    // Aliased rather than recomputed so the two can never drift apart — the
    // height cell missing this gate for 14 months is exactly what drift costs.
    const measurementsPublic = measurementsVisible;
    if (
      measurementsPublic &&
      d.bust_cm &&
      isFieldVisible('bustCm', viewContext)
    ) {
      cells.push({
        label: t('dancer:publicView.identityStrip.bust'),
        value: `${d.bust_cm} cm`,
      });
    }
    if (
      measurementsPublic &&
      d.waist_cm &&
      isFieldVisible('waistCm', viewContext)
    ) {
      cells.push({
        label: t('dancer:publicView.identityStrip.waist'),
        value: `${d.waist_cm} cm`,
      });
    }
    if (
      measurementsPublic &&
      d.hips_cm &&
      isFieldVisible('hipsCm', viewContext)
    ) {
      cells.push({
        label: t('dancer:publicView.identityStrip.hips'),
        value: `${d.hips_cm} cm`,
      });
    }
  }

  return cells;
}

/**
 * Compose the affiliation cell for the identity strip.
 *
 * - Professional: "Company -- Rank", "Company", or "Freelancer".
 * - Student: current school name.
 * - Amateur / null: returns null (no cell rendered).
 */
function composeAffiliation(
  d: PublicDancerProfile,
  experiences: DancerExperience[] | undefined,
  t: TFunction,
): Cell | null {
  if (d.profile_category === 'professional') {
    const current = pickCurrentByType(experiences, 'company');
    if (current) {
      const parts = [current.organization_manual, current.rank_title].filter(
        (v): v is string => !!v && v.trim().length > 0,
      );
      if (parts.length > 0) {
        return {
          label: t('dancer:publicView.identityStrip.affiliation'),
          value: parts.join(' — '),
        };
      }
    }
    if (d.is_freelancer) {
      return {
        label: t('dancer:publicView.identityStrip.affiliation'),
        value: t('dancer:publicView.identityStrip.freelancer'),
      };
    }
    return null;
  }

  if (d.profile_category === 'student') {
    const current = pickCurrentByType(experiences, 'school');
    if (current?.organization_manual) {
      return {
        label: t('dancer:publicView.identityStrip.school'),
        value: current.organization_manual,
      };
    }
    return null;
  }

  return null;
}

/**
 * Pick the most relevant current experience of a given type: explicit
 * `current === true` first, then open-ended (no end_date), filtered to type.
 */
function pickCurrentByType(
  experiences: DancerExperience[] | undefined,
  type: string,
): DancerExperience | null {
  if (!experiences || experiences.length === 0) return null;
  const typed = experiences.filter((e) => e.type === type);
  if (typed.length === 0) return null;
  const flagged = typed.find((e) => e.current === true);
  if (flagged) return flagged;
  const openEnded = typed.find((e) => !e.end_date);
  return openEnded ?? null;
}

function capitalise(s: string): string {
  if (!s) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}

/** Format height as `${cm} cm · ${ft}'${in}"`. 1 cm = 0.3937 in;
 * round to the nearest inch then split into feet + remainder. */
function formatHeight(cm: number): string {
  const totalIn = Math.round(cm / 2.54);
  const ft = Math.floor(totalIn / 12);
  const inches = totalIn % 12;
  return `${cm} cm · ${ft}′${inches}″`;
}
